import * as React from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale/pt-BR";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DateInput } from "@/components/ui/date-input";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { cn } from "@/lib/utils";
import { useFinance } from "@/context/FinanceContext";
import { CategorySelector } from "@/components/CategorySelector";
import { AccountSelector } from "@/components/AccountSelector";
import { CostCenterSelector } from "@/components/CostCenterSelector";
import { PaymentMethodSelector } from "@/components/PaymentMethodSelector";
import { SupplierSelector } from "@/components/SupplierSelector";
import { useToast } from "@/hooks/use-toast";

const schema = z.object({
  description: z.string().trim().min(1, "Descrição é obrigatória"),
  value: z.string().refine((v) => parseFloat(v) > 0, "Valor deve ser maior que zero"),
  type: z.enum(["receita", "despesa"]),
  categoryId: z.string().min(1, "Plano de contas é obrigatório"),
  accountId: z.string().min(1, "Conta é obrigatória"),
  
  costCenterId: z.string().min(1, "Centro de custos é obrigatório"),
  paymentMethodId: z.string().min(1, "Forma de pagamento é obrigatória"),  
  supplierId: z.string().min(1, "Fornecedor é obrigatório"),
  
  issueDate: z.string().min(1, "Data de emissão é obrigatória"),
  dueDate: z.string().min(1, "Data de vencimento é obrigatória"),
  paymentDate: z.string().optional(),
  parcelMode: z.enum(["total", "parcela"]),
  parcels: z.number().int().min(1).max(60).optional().nullable(),
  issueDateMode: z.enum(["fixed", "followDue"]).optional(),
  settled: z.boolean().optional(),
}).refine(
  (data) => !data.settled || (data.paymentDate && data.paymentDate.trim() !== ""),
  {
    message: "Data de pagamento é obrigatória quando o lançamento está marcado como pago",
    path: ["paymentDate"],
  }
);

export type TransactionFormValues = z.infer<typeof schema>;

export interface TransactionFormProps {
  defaultValues?: Partial<TransactionFormValues>;
  onSubmitted?: () => void;
  transactionId?: string;
  lockType?: "receita" | "despesa";
}

export const TransactionForm: React.FC<TransactionFormProps> = ({
  defaultValues,
  onSubmitted,
  transactionId,
  lockType,
}) => {
  const { addTransaction, updateTransaction, categories, accounts, costCenters, paymentMethods, suppliers, addSupplier, addCostCenter, addPaymentMethod, addAccount } = useFinance();
  const { toast } = useToast();
  
  // Refs para navegação por Enter
  const descriptionRef = React.useRef<HTMLInputElement>(null);
  const valueRef = React.useRef<HTMLInputElement>(null);

  const form = useForm<TransactionFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      description: defaultValues?.description ?? "",
      value: String(defaultValues?.value ?? ""),
      type: lockType ?? defaultValues?.type ?? "despesa",
      categoryId: defaultValues?.categoryId ?? "",
      accountId: defaultValues?.accountId ?? "",
      
      costCenterId: (defaultValues as any)?.costCenterId ?? "",
      paymentMethodId: (defaultValues as any)?.paymentMethodId ?? "",
      supplierId: (defaultValues as any)?.supplierId ?? "",
      
      issueDate: defaultValues?.issueDate ?? "",
      dueDate: defaultValues?.dueDate ?? "",
      paymentDate: defaultValues?.paymentDate ?? "",
      parcelMode: (defaultValues?.parcelMode ?? "total") as "total" | "parcela",
      parcels: defaultValues?.parcels ?? null,
      issueDateMode: ((defaultValues as any)?.issueDateMode ?? "fixed") as "fixed" | "followDue",
      settled: defaultValues?.settled ?? false,
    },
  });

  React.useEffect(() => {
    form.register("accountId");
  }, [form]);

  const parcelMode = form.watch("parcelMode");

  // Navegação por Enter nos campos
  const handleEnterNavigation = (nextRef: React.RefObject<HTMLInputElement | HTMLButtonElement>) => {
    setTimeout(() => {
      nextRef.current?.focus();
    }, 100);
  };


  const submit = (values: TransactionFormValues) => {
    try {
      const parcelCount = values.parcelMode === "parcela" ? values.parcels ?? 1 : 1;

      // EDIÇÃO: quando há transactionId, sempre atualiza UMA única transação
      // com os valores exatos do form (sem loop, sem recalcular datas via
      // new Date()/toISOString — evita deslocamento por timezone UTC-3).
      if (transactionId) {
        const transaction = {
          description: values.description,
          value: parseFloat(values.value),
          type: values.type,
          categoryId: values.categoryId,
          accountId: values.accountId || "",
          costCenterId: values.costCenterId || undefined,
          paymentMethodId: values.paymentMethodId || undefined,
          supplierId: values.supplierId || undefined,
          issueDate: values.issueDate,
          dueDate: values.dueDate,
          paymentDate: values.settled ? (values.paymentDate || undefined) : undefined,
          settled: values.settled ?? false,
          issueDateMode: values.issueDateMode || "fixed",
        };
        console.log('📝 [TransactionForm] Editando transação', transactionId, transaction);
        updateTransaction(transactionId, transaction);
      } else if (parcelCount === 1) {
        const transaction = {
          description: values.description,
          value: parseFloat(values.value),
          type: values.type,
          categoryId: values.categoryId,
          accountId: values.accountId || "",
          costCenterId: values.costCenterId || undefined,
          paymentMethodId: values.paymentMethodId || undefined,
          supplierId: values.supplierId || undefined,
          issueDate: values.issueDate,
          dueDate: values.dueDate,
          paymentDate: values.settled ? (values.paymentDate || undefined) : undefined,
          settled: values.settled ?? false,
          parcelMode: "total" as const,
          parcels: null,
          parcelIndex: null,
          issueDateMode: values.issueDateMode || "fixed",
        };
        addTransaction(transaction);
      } else {
        // Criação de múltiplas parcelas (somente quando NÃO há transactionId)
        // Parse manual de YYYY-MM-DD para evitar shift de timezone (UTC-3)
        const [byStr, bmStr, bdStr] = (values.dueDate || "").split("-");
        const baseYear = parseInt(byStr, 10);
        const baseMonth = parseInt(bmStr, 10); // 1-12
        const baseDay = parseInt(bdStr, 10);

        const [iyStr, imStr, idStr] = (values.issueDate || "").split("-");
        const issueYear = parseInt(iyStr, 10);
        const issueMonth = parseInt(imStr, 10);
        const issueDay = parseInt(idStr, 10);

        const pad = (n: number) => String(n).padStart(2, "0");
        const daysInMonth = (y: number, m: number) => new Date(y, m, 0).getDate(); // m 1-12
        const addMonthsPreserveDay = (y: number, m: number, d: number, add: number) => {
          const total = (m - 1) + add;
          const ny = y + Math.floor(total / 12);
          const nm = (total % 12 + 12) % 12 + 1;
          const nd = Math.min(d, daysInMonth(ny, nm));
          return `${ny}-${pad(nm)}-${pad(nd)}`;
        };

        for (let i = 0; i < parcelCount; i++) {
          const parcelDueDate = addMonthsPreserveDay(baseYear, baseMonth, baseDay, i);
          const parcelIssueDate = values.issueDateMode === "followDue"
            ? addMonthsPreserveDay(issueYear || baseYear, issueMonth || baseMonth, issueDay || baseDay, i)
            : values.issueDate;

          const transaction = {
            description: values.description,
            value: parseFloat(values.value),
            type: values.type,
            categoryId: values.categoryId,
            accountId: values.accountId || "",
            costCenterId: values.costCenterId || undefined,
            paymentMethodId: values.paymentMethodId || undefined,
            supplierId: values.supplierId || undefined,
            issueDate: parcelIssueDate,
            dueDate: parcelDueDate,
            paymentDate: values.settled ? (values.paymentDate || parcelDueDate) : undefined,

            settled: values.settled ?? false,
            parcelMode: "parcela" as const,
            parcels: parcelCount,
            parcelIndex: i + 1,
            issueDateMode: values.issueDateMode || "fixed",
          };
          addTransaction(transaction);
        }
      }
      
      toast({ title: "Transação salva com sucesso!", duration: 1000 });
      onSubmitted?.();
      form.reset();
    } catch (error) {
      console.error("Error submitting transaction:", error);
      toast({ title: "Erro ao salvar transação", variant: "destructive", duration: 1000 });
    }
  };

  React.useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === "type") {
        const hasMatchingCategory = categories.find(
          (c) => c.type === value.type && c.id === value.categoryId
        );
        if (!hasMatchingCategory) {
          form.setValue("categoryId", "", { shouldValidate: true });
        }
      }
      // Remove payment date when status is pending
      if (name === "settled" && value.settled === false) {
        form.setValue("paymentDate", "");
      }
    });
    return () => subscription.unsubscribe();
  }, [form, categories]);

  const availableCategories = categories.filter(
    (cat) => !cat.hidden && (!cat.type || cat.type === form.watch("type"))
  );

  // Handlers para cadastro rápido
  const handleAddAccount = async (name: string) => {
    try {
      const newAccountId = await addAccount(name);
      if (newAccountId) {
        form.setValue("accountId", newAccountId, { shouldValidate: true });
      }
    } catch (error) {
      console.error('Error adding account:', error);
    }
  };

  const handleAddCostCenter = async (name: string) => {
    try {
      const newCostCenterId = await addCostCenter(name);
      if (newCostCenterId) {
        form.setValue("costCenterId", newCostCenterId, { shouldValidate: true });
      }
    } catch (error) {
      console.error('Error adding cost center:', error);
    }
  };

  const handleAddPaymentMethod = async (name: string) => {
    try {
      const newPaymentMethodId = await addPaymentMethod(name);
      if (newPaymentMethodId) {
        form.setValue("paymentMethodId", newPaymentMethodId, { shouldValidate: true });
      }
    } catch (error) {
      console.error('Error adding payment method:', error);
    }
  };

  const handleAddSupplier = async (name: string) => {
    try {  
      const newSupplierId = await addSupplier(name);
      if (newSupplierId) {
        form.setValue("supplierId", newSupplierId, { shouldValidate: true });
      }
    } catch (error) {
      console.error('Error adding supplier:', error);
    }
  };

  return (
    <form onSubmit={form.handleSubmit(submit)} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">

        <div className="md:col-span-2 lg:col-span-3 space-y-2">
          <Label htmlFor="description" className="text-sm font-medium text-foreground">
            Descrição *
          </Label>
          <Input
            ref={descriptionRef}
            {...form.register("description")}
            placeholder="Ex: Conta de luz"
            onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
            aria-invalid={!!form.formState.errors.description}
            className={cn("border", form.formState.errors.description && "border-destructive focus-visible:ring-destructive")}
            id="description"
          />
          {form.formState.errors.description && (
            <div className="text-sm text-destructive">{form.formState.errors.description.message}</div>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="value" className="text-sm font-medium text-foreground">
            Valor *
          </Label>
          <Input
            ref={valueRef}
            {...form.register("value")}
            type="number"
            step="0.01"
            placeholder="0,00"
            onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
            aria-invalid={!!form.formState.errors.value}
            className={cn("border", form.formState.errors.value && "border-destructive focus-visible:ring-destructive")}
            id="value"
          />
          {form.formState.errors.value && (
            <div className="text-sm text-destructive">{form.formState.errors.value.message}</div>
          )}
        </div>

        {!lockType && !transactionId && (
          <div className="space-y-2">
            <Label className="text-sm font-medium text-foreground">Tipo *</Label>
            <div className="w-full">
              <ToggleGroup type="single" variant="outline" value={form.watch("type")} onValueChange={(v) => { if (v) form.setValue("type", v as "receita" | "despesa", { shouldValidate: true, shouldDirty: true, shouldTouch: true }) }} className="w-full">
                <ToggleGroupItem 
                  value="receita"
                  className="flex-1 text-sm"
                  disabled={lockType === "despesa"}
                >
                  Receita
                </ToggleGroupItem>
                <ToggleGroupItem 
                  value="despesa"
                  className="flex-1 text-sm"
                  disabled={lockType === "receita"}
                >
                  Despesa
                </ToggleGroupItem>
              </ToggleGroup>
            </div>
          </div>
        )}

        <CategorySelector
          categories={availableCategories}
          value={form.watch("categoryId")}
          onValueChange={(value) => form.setValue("categoryId", value, { shouldValidate: true })}
          error={form.formState.errors.categoryId?.message}
          filterType={form.watch("type")}
          canAddNew={false}
        />

        <AccountSelector
          accounts={accounts}
          value={form.watch("accountId")}
          onValueChange={(value) => form.setValue("accountId", value, { shouldValidate: true })}
          error={form.formState.errors.accountId?.message}
          canAddNew={true}
          onAddNew={handleAddAccount}
        />

        <CostCenterSelector
          costCenters={costCenters}
          value={form.watch("costCenterId")}
          onValueChange={(value) => form.setValue("costCenterId", value, { shouldValidate: true })}
          error={form.formState.errors.costCenterId?.message}
          canAddNew={true}
          onAddNew={handleAddCostCenter}
        />

        <PaymentMethodSelector
          paymentMethods={paymentMethods}
          value={form.watch("paymentMethodId")}
          onValueChange={(value) => form.setValue("paymentMethodId", value, { shouldValidate: true })}
          error={form.formState.errors.paymentMethodId?.message}
          canAddNew={true}
          onAddNew={handleAddPaymentMethod}
        />

        <SupplierSelector
          suppliers={suppliers}
          value={form.watch("supplierId")}
          onValueChange={(value) => form.setValue("supplierId", value, { shouldValidate: true })}
          error={form.formState.errors.supplierId?.message}
          canAddNew={true}
          onAddNew={handleAddSupplier}
        />

        {/* Data fields - usando DateInput como no PDV */}
        <div className="space-y-2">
          <Label className="text-sm font-medium text-foreground">Data de Emissão *</Label>
          <DateInput
            value={form.watch("issueDate") || ""}
            onChange={(value) => form.setValue("issueDate", value || "")}
            placeholder="DD/MM/YYYY"
          />
          {form.formState.errors.issueDate && (
            <p className="text-sm text-destructive">{form.formState.errors.issueDate.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label className="text-sm font-medium text-foreground">Data de Vencimento *</Label>
          <DateInput
            value={form.watch("dueDate") || ""}
            onChange={(value) => form.setValue("dueDate", value || "")}
            placeholder="DD/MM/YYYY"
          />
          {form.formState.errors.dueDate && (
            <p className="text-sm text-destructive">{form.formState.errors.dueDate.message}</p>
          )}
        </div>

        {form.watch("settled") && (
          <div className="space-y-2">
            <Label className="text-sm font-medium text-foreground">Data de Pagamento *</Label>
            <DateInput
              value={form.watch("paymentDate") || ""}
              onChange={(value) => {
                form.setValue("paymentDate", value || "");
                form.trigger("paymentDate");
              }}
              placeholder="DD/MM/YYYY"
            />
            {form.formState.errors.paymentDate && (
              <p className="text-sm text-destructive">{form.formState.errors.paymentDate.message}</p>
            )}
          </div>
        )}

        {/* Parcel options */}
        {!transactionId && (
          <>
            <div className="space-y-2">
              <Label className="text-sm font-medium text-foreground">Parcelamento</Label>
              <ToggleGroup 
                type="single" 
                variant="outline" 
                value={parcelMode} 
                onValueChange={(v) => { if (v) form.setValue("parcelMode", v as "total" | "parcela") }}
                className="w-full"
              >
                <ToggleGroupItem value="total" className="flex-1 text-sm">À Vista</ToggleGroupItem>
                <ToggleGroupItem value="parcela" className="flex-1 text-sm">Parcelado</ToggleGroupItem>
              </ToggleGroup>
            </div>

            {parcelMode === "parcela" && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="parcels" className="text-sm font-medium text-foreground">Parcelas</Label>
                  <Input
                    {...form.register("parcels", { valueAsNumber: true })}
                    type="number"
                    min="2"
                    max="60"
                    placeholder="2"
                    id="parcels"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-foreground">Data de Emissão</Label>
                  <ToggleGroup 
                    type="single" 
                    variant="outline" 
                    value={form.watch("issueDateMode") || "fixed"} 
                    onValueChange={(v) => { if (v) form.setValue("issueDateMode", v as "fixed" | "followDue") }}
                    className="w-full"
                  >
                    <ToggleGroupItem value="fixed" className="flex-1 text-sm">Fixa</ToggleGroupItem>
                    <ToggleGroupItem value="followDue" className="flex-1 text-sm">Acompanhar Vencimento</ToggleGroupItem>
                  </ToggleGroup>
                </div>
              </>
            )}
          </>
        )}

      </div>

      <div className="flex items-center space-x-2">
        <input
          {...form.register("settled")}
          type="checkbox"
          id="settled"
          className="rounded border-gray-300"
        />
        <Label htmlFor="settled" className="text-sm font-medium text-foreground">
          Pago
        </Label>
      </div>

      <div className="flex justify-end">
        <Button type="submit" className="min-w-32">
          {transactionId ? "Atualizar" : "Salvar"}
        </Button>
      </div>
    </form>
  );
};